// ÄNDERUNG: useEffect für canPay grundlegend optimiert (debounce + stabile Listener)
// - Listener werden nur noch bei Änderung von billingSame neu gebunden
// - DOM-Elemente werden gecacht, keine wiederholten getElementById-Looks pro Keystroke
// - checkAll ist debounced, setCanPay wird nur bei Wertänderung gesetzt
// - Separater Effekt reagiert auf Stripe-/Loader-Status ohne Listener neu zu binden

import styled from "styled-components/macro";
import React, { useEffect, useRef, useState } from "react"; // ÄNDERUNG: useRef importiert
import { useNavigate } from "react-router";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import "../styles/Checkout.css";
import { isEmpty } from "../functions/isEmpty";
import {
  MdArrowBackIosNew,
  MdArrowForwardIos,
  MdCheck,
  MdMenu,
} from "react-icons/md";
import { formatEuroCents } from "../functions/formatEuroCents";

export default function StripeCheckoutForm({
  apiURL,
  isMobile,
  authToken,
  order,
  setReload,
  reload,
  companyData,
  orderData,
  setOrderData,
  payment_id,
  cart,
  userID,
  billingSame,
  setBillingSame,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [peReady, setPeReady] = useState(false); // wurde gerendert/ist bereit
  const [peComplete, setPeComplete] = useState(false); // alle Pflichtfelder gültig
  // HINZUFÜGEN: aktuell ausgewählter Payment-Typ (z. B. 'card', 'paypal', 'sofort', ...)

  const DEFAULT_PM_TYPE = "paypal";
  const [pmType, setPmType] = useState(DEFAULT_PM_TYPE);
  const [canPay, setCanPay] = useState(false);

  // [NEU] Kleine Helper-Funktion: Wert eines Inputs lesen (leer -> "")
  const val = (id) => document.getElementById(id)?.value?.trim() ?? "";

  // HINZUFÜGEN: Methoden, die ohne "complete" auskommen (Wallet/Redirect)
  const isWalletOrRedirect = (type) => {
    if (!type) return false;
    // Liste nach Bedarf erweitern/verschlanken – Kernpunkt: paypal hier drin
    const redirects = [
      "paypal",
      "sofort",
      "ideal",
      "bancontact",
      "giropay",
      "alipay",
      "eps",
      "p24",
      "blik",
      "grabpay",
      "wechat_pay",
      "oxxo",
      "boleto",
    ];
    return redirects.includes(type);
  };

  // ──────────────────────────────────────────────────────────────────────────────
  // ÄNDERUNG: Optimierter Input-Check mit stabilen Listenern + Debounce
  // ──────────────────────────────────────────────────────────────────────────────
  const requiredShippingRef = useRef([
    "shipping_firstName",
    "shipping_lastName",
    "shipping_street",
    "shipping_houseNumber",
    "shipping_zip",
    "shipping_city",
    "email",
  ]);
  const requiredBillingStatic = [
    "billing_firstName",
    "billing_lastName",
    "billing_street",
    "billing_houseNumber",
    "billing_zip",
    "billing_city",
  ];

  const idsRef = useRef(new Set()); // Set der beobachteten Input-IDs

  const debounceRef = useRef(null); // debounce timer id

  // [NEU] zentrale Berechnung (liest aus elMapRef + aktuelle Stripe-Flags)
  const computeCanPay = () => {
    const idsShipping = requiredShippingRef.current;
    const idsBilling = billingSame ? [] : requiredBillingStatic;

    const nonEmpty = (id) =>
      (document.getElementById(id)?.value?.trim() ?? "") !== "";

    const shippingOK = idsShipping.every(nonEmpty);
    const billingOK = billingSame ? true : idsBilling.every(nonEmpty);
    // Basis-Ready:
    // - Karte: braucht Stripe+Elements und darf nicht laden
    // - Wallet/Redirect (PayPal & Co.): darf UI-seitig freigeben, auch wenn Stripe/Elements noch nicht bereit
    const isWallet = isWalletOrRedirect(pmType);
    const stripeBaseReady =
      !isLoading && (isWallet || (!!stripe && !!elements));
    // Zahlungsdetails:
    // - Wallet/Redirect: keine zusätzlichen Form-Felder nötig
    // - Karte: PaymentElement muss bereit + complete sein
    const paymentDetailsReady = isWallet ? true : peReady && peComplete;

    const next =
      shippingOK && billingOK && stripeBaseReady && paymentDetailsReady;
    setCanPay((prev) => (prev !== next ? next : prev));

    // Debug:
    console.log({
      shippingOK,
      billingOK,
      stripeBaseReady,
      peReady,
      peComplete,
      pmType,
      isWallet: isWalletOrRedirect(pmType),
      next,
    });
  };

  // [NEU] Debounced Trigger
  const scheduleCheck = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(computeCanPay, 120);
  };

  useEffect(() => {
    // beobachtete IDs aktualisieren
    idsRef.current = new Set([
      ...requiredShippingRef.current,
      ...(!billingSame ? requiredBillingStatic : []),
    ]);

    const handler = (e) => {
      const id = e?.target?.id;
      if (id && idsRef.current.has(id)) scheduleCheck();
    };

    // Capture-Phase, damit auch bei verschachtelten/gruppierten Inputs sicher gefeuert wird
    document.addEventListener("input", handler, true);
    document.addEventListener("change", handler, true);
    document.addEventListener("keyup", handler, true);
    document.addEventListener("blur", handler, true);

    // initialer Check (z. B. bei Autofill, bereits ausgefüllten Feldern)
    scheduleCheck();

    return () => {
      document.removeEventListener("input", handler, true);
      document.removeEventListener("change", handler, true);
      document.removeEventListener("keyup", handler, true);
      document.removeEventListener("blur", handler, true);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [billingSame]);

  useEffect(() => {
    // 2 kurze Checks nach Mount (0ms/250ms), falls Stripe/DOM minimal später bereit ist
    const t1 = setTimeout(scheduleCheck, 0);
    const t2 = setTimeout(scheduleCheck, 350);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // [NEU] Rechne nur neu, wenn Stripe-/Loader-Status wechseln – ohne Listener neu zu binden
  useEffect(() => {
    scheduleCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, elements, peReady, peComplete, isLoading, pmType]);

  // ──────────────────────────────────────────────────────────────────────────────

  const placeOrder = async () => {
    // Guard gegen Doppelklicks
    if (isLoading) return;
    setIsLoading(true);
    let now = Math.floor(Date.now() / 1000);

    // hier der wert von jedem input
    const emailValue = document.getElementById("email")?.value?.trim() || ""; // falls Email-Feld genutzt

    const shipping = {
      firstName:
        document.getElementById("shipping_firstName")?.value?.trim() || "",
      lastName:
        document.getElementById("shipping_lastName")?.value?.trim() || "",
      company: document.getElementById("shipping_company")?.value?.trim() || "",
      street: document.getElementById("shipping_street")?.value?.trim() || "",
      houseNumber:
        document.getElementById("shipping_houseNumber")?.value?.trim() || "",
      zip: document.getElementById("shipping_zip")?.value?.trim() || "",
      city: document.getElementById("shipping_city")?.value?.trim() || "",
      country: "DE",
    };

    const billing = billingSame
      ? { ...shipping }
      : {
          firstName:
            document.getElementById("billing_firstName")?.value?.trim() || "",
          lastName:
            document.getElementById("billing_lastName")?.value?.trim() || "",
          company:
            document.getElementById("billing_company")?.value?.trim() || "",
          street:
            document.getElementById("billing_street")?.value?.trim() || "",
          houseNumber:
            document.getElementById("billing_houseNumber")?.value?.trim() || "",
          zip: document.getElementById("billing_zip")?.value?.trim() || "",
          city: document.getElementById("billing_city")?.value?.trim() || "",
          country: "DE",
        };

    // flaches Objekt für den Request-Body; jeder Input als eigener Key
    const bodyPayload = {
      id: payment_id,
      timestamp_unix: now,
      users_id: userID ?? null,
      email: emailValue,

      // Shipping (flach)
      shipping_firstName: shipping.firstName,
      shipping_lastName: shipping.lastName,
      shipping_company: shipping.company,
      shipping_street: shipping.street,
      shipping_houseNumber: shipping.houseNumber,
      shipping_zip: shipping.zip,
      shipping_city: shipping.city,
      shipping_country: shipping.country,

      // Billing (flach)
      billing_firstName: billing.firstName,
      billing_lastName: billing.lastName,
      billing_company: billing.company,
      billing_street: billing.street,
      billing_houseNumber: billing.houseNumber,
      billing_zip: billing.zip,
      billing_city: billing.city,
      billing_country: billing.country,

      billing_same_as_shipping: !!billingSame,
    };

    try {
      const resa = await fetch(`${apiURL}/payments/new_order.php`, {
        method: "PUT",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          // Authorization: `Bearer ${authToken?.JWT}`,
        },
        body: JSON.stringify(bodyPayload),
      });

      if (!resa.ok) {
        const text = await resa.text().catch(() => "");
        throw new Error(`HTTP ${resa.status} ${resa.statusText} ${text}`);
      }
      handleSubmit();
    } catch (err) {
      setErrorMessage(
        "Bestellung konnte nicht übermittelt werden. Bitte später erneut versuchen."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);

    if (!stripe || !elements) {
      setErrorMessage("Stripe ist noch nicht geladen.");
      setIsLoading(false);
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/ordercomplete?success`,
      },
    });

    if (error) {
      setErrorMessage(error.message);
    } else {
      //  setReload(!reload);
    }
    setIsLoading(false);
  };

  const getTotalPriceFromCart = (cart) => {
    let count = 0;
    if (!isEmpty(cart)) {
      cart.map((item) => {
        count += item.price;
      });

      return count;
    }
  };

  return (
    <div className="">
      <div className="checkout_section_hero_right_delivery">
        <div className="checkout_section_hero_right_delivery_title">
          Lieferadresse
        </div>
        <div className="checkout_section_hero_right_delivery_inputs">
          <div className="checkout_section_hero_right_delivery_inputs_halfrow">
            <input
              className="checkout_section_hero_right_delivery_inputs_halfrow_input"
              placeholder="Vorname"
              id="shipping_firstName"
            />
            <input
              className="checkout_section_hero_right_delivery_inputs_halfrow_input"
              placeholder="Nachname"
              id="shipping_lastName"
            />
          </div>
          <div className="checkout_section_hero_right_delivery_inputs_fullrow">
            <input
              className="checkout_section_hero_right_delivery_inputs_fullrow_input"
              placeholder="Firmenname (optional)"
              id="shipping_company"
            />
          </div>
          <div className="checkout_section_hero_right_delivery_inputs_customrow">
            <input
              className="checkout_section_hero_right_delivery_inputs_customrow_input_l"
              placeholder="Straßenname"
              id="shipping_street"
            />
            <input
              className="checkout_section_hero_right_delivery_inputs_customrow_input_s"
              placeholder="Hausnr."
              id="shipping_houseNumber"
            />
          </div>
          <div className="checkout_section_hero_right_delivery_inputs_halfrow">
            <input
              className="checkout_section_hero_right_delivery_inputs_halfrow_input"
              placeholder="Postleitzahl"
              id="shipping_zip"
            />
            <input
              className="checkout_section_hero_right_delivery_inputs_halfrow_input"
              placeholder="Stadt"
              id="shipping_city"
            />
          </div>
          <div className="checkout_section_hero_right_delivery_inputs_fullrow">
            <input
              className="checkout_section_hero_right_delivery_inputs_fullrow_input"
              placeholder="Emailadresse"
              id="email"
            />
          </div>
          Derzeit liefern wir nur innerhalb Deutschlands.
        </div>
        <div className="checkout_section_hero_right_billing">
          <div className="checkout_section_hero_right_billing_btnrow">
            <div className="checkout_section_hero_right_billing_btnrow_title">
              Rechnungsadresse ist wie Lieferadresse
            </div>
            <div
              className="checkout_section_hero_right_billing_btnrow_btn"
              onClick={() => setBillingSame(!billingSame)}>
              {billingSame && <CheckIcon />}
            </div>
          </div>

          {!billingSame && (
            <>
              <div className="checkout_section_hero_right_delivery_title">
                Rechnungsadresse
              </div>
              <div className="checkout_section_hero_right_billing_inputs">
                <div className="checkout_section_hero_right_delivery_inputs_halfrow">
                  <input
                    className="checkout_section_hero_right_delivery_inputs_halfrow_input"
                    placeholder="Vorname"
                    id="billing_firstName"
                  />
                  <input
                    className="checkout_section_hero_right_delivery_inputs_halfrow_input"
                    placeholder="Nachname"
                    id="billing_lastName"
                  />
                </div>
                <div className="checkout_section_hero_right_delivery_inputs_fullrow">
                  <input
                    className="checkout_section_hero_right_delivery_inputs_fullrow_input"
                    placeholder="Firmenname (optional)"
                    id="billing_company"
                  />
                </div>
                <div className="checkout_section_hero_right_delivery_inputs_customrow">
                  <input
                    className="checkout_section_hero_right_delivery_inputs_customrow_input_l"
                    placeholder="Straßenname"
                    id="billing_street"
                  />
                  <input
                    className="checkout_section_hero_right_delivery_inputs_customrow_input_s"
                    placeholder="Hausnr."
                    id="billing_houseNumber"
                  />
                </div>
                <div className="checkout_section_hero_right_delivery_inputs_halfrow">
                  <input
                    className="checkout_section_hero_right_delivery_inputs_halfrow_input"
                    placeholder="Postleitzahl"
                    id="billing_zip"
                  />
                  <input
                    className="checkout_section_hero_right_delivery_inputs_halfrow_input"
                    placeholder="Stadt"
                    id="billing_city"
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="checkout_section_hero_right_payment">
          <div className="checkout_section_hero_right_payment_title">
            Zahlung
          </div>

          <PaymentElement
            onReady={() => {
              setPeReady(true);
              // wichtig, damit ohne User-Interaktion gleich geprüft wird
              try {
                scheduleCheck();
              } catch {}
            }}
            onLoaderStart={() => setPeReady(false)}
            onChange={(event) => {
              setPeComplete(!!event.complete);
              setErrorMessage(event.error?.message || "");
              setPmType(event?.value?.type ?? null);
            }}
          />
        </div>

        <div className="checkout_section_hero_right_payment_cta">
          <div
            className={
              "checkout_section_hero_right_payment_cta_btn" +
              (!canPay ? "_disabled" : "")
            }
            onClick={() => {
              if (canPay) placeOrder();
            }}>
            {isLoading
              ? "Verarbeite Zahlung …"
              : !isWalletOrRedirect(pmType) && (!stripe || !elements)
              ? "Lädt Stripe …"
              : !isWalletOrRedirect(pmType) && !peReady
              ? "Zahlungsformular lädt …"
              : !(isWalletOrRedirect(pmType) || peComplete)
              ? "Bitte Zahlungsdaten ausfüllen"
              : "Bezahlen"}
          </div>
          {errorMessage && (
            <div className="checkout_error" role="alert">
              {errorMessage}
            </div>
          )}
        </div>
        <div className="checkout_section_hero_right_agb">
          Mit Klicken auf "Bestellen" bestellst du zahlungspflichtig und
          akzeptierst die AGB.
        </div>
      </div>
    </div>
  );
}

const MenuIcon = styled(MdMenu)`
  font-size: 30px;
  color: #474747;
`;

const ArrowForwardIcon = styled(MdArrowForwardIos)`
  font-size: 18px;
  color: #474747;
`;

const ArrowBack = styled(MdArrowBackIosNew)`
  font-size: 23px;
  color: #474747;
`;

const ArrowBackDesktop = styled(MdArrowBackIosNew)`
  font-size: 23px;
  color: #474747;
  margin-bottom: -5px;
  margin-right: 5px;
`;
const CheckIcon = styled(MdCheck)`
  font-size: 20px;
  color: #004a78;
`;
